import type React from "react";
import { Children, isValidElement, type FC } from "react";
import {
    SelectProvider as AriakitSelectProvider,
    Select as AriakitSelect,
    SelectPopover as AriakitSelectPopover,
    SelectItem as AriakitSelectItem,
    SelectItemCheck as AriakitSelectItemCheck,
} from "@ariakit/react";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { IconChevronDownSmall } from "@/assets/icons/geist/IconChevronDownSmall";
import { IconCheckmark2Small } from "central-icons/IconCheckmark2Small";
import type { PropsWithTestId } from "@/components/podkit/lib/testid";

/**
 * Component displayName constants for Fast Refresh compatibility.
 */
const SELECT_ITEM = "Select.Item";

/**
 * Props for the root Select component.
 */
export type SelectProps = PropsWithClassName &
    PropsWithTestId & {
        /** The default value for uncontrolled usage */
        defaultValue?: string;
        /** The current value for controlled usage */
        value?: string;
        /** Callback when value changes */
        onValueChange?: (value: string) => void;
        /** Whether the select is disabled */
        disabled?: boolean;
        /** Whether the select is in loading state */
        loading?: boolean;
        /** Placeholder text when no value is selected */
        placeholder?: string;
        /** Custom function to format the display value in the trigger */
        formatDisplayValue?: (value: string, label: React.ReactNode) => React.ReactNode;
    };

/**
 * Props for Select.Item component.
 */
export type SelectItemProps = React.ComponentProps<typeof AriakitSelectItem> & {
    /** The value of this item */
    value: string;
};

/**
 * Helper function to get the displayName from a React element.
 */
const getComponentDisplayName = (element: React.ReactElement): string | undefined => {
    const type = element.type as React.ComponentType;
    return type.displayName;
};

/**
 * A simple select dropdown component built on Ariakit.
 * Compatible with Combobox for proper state coordination.
 *
 * @example
 * ```tsx
 * <Select value={value} onValueChange={setValue}>
 *   <Select.Item value="option1">Option 1</Select.Item>
 *   <Select.Item value="option2">Option 2</Select.Item>
 * </Select>
 * ```
 */
const Select: FC<React.PropsWithChildren<SelectProps>> & SelectSlots = ({
    children,
    defaultValue,
    value,
    onValueChange,
    disabled = false,
    loading = false,
    placeholder = "Select...",
    formatDisplayValue,
    "data-testid": dataTestId,
    className,
}) => {
    // Extract items from children
    const items: React.ReactElement[] = [];
    Children.forEach(children, (child) => {
        if (isValidElement(child) && getComponentDisplayName(child) === SELECT_ITEM) {
            items.push(child);
        }
    });

    // Find the selected item to display its label
    const selectedItem = items.find((item) => item.props.value === value);
    const itemLabel = (selectedItem?.props.children as React.ReactNode) || placeholder;
    const displayValue = formatDisplayValue && value ? formatDisplayValue(value, itemLabel) : itemLabel;

    return (
        <AriakitSelectProvider defaultValue={defaultValue} value={value} setValue={onValueChange}>
            <AriakitSelect
                data-testid={dataTestId}
                disabled={disabled || loading}
                className={cn(
                    "flex h-9 items-center justify-between gap-2",
                    "rounded-lg border border-border-base bg-surface-pure px-3",
                    "text-base text-content-primary",
                    "transition-all duration-150 ease-out",
                    "outline-none",
                    // Only show focus ring when opened (aria-expanded), not on regular focus
                    "aria-expanded:border-border-invert aria-expanded:ring-1 aria-expanded:ring-border-brand aria-expanded:ring-offset-1",
                    "disabled:cursor-not-allowed disabled:opacity-50",
                    className,
                )}
            >
                <span className={cn("truncate", { "opacity-50 blur-[1px]": loading })}>{displayValue}</span>
                <IconChevronDownSmall size="sm" className="flex-none text-content-primary" />
            </AriakitSelect>
            <AriakitSelectPopover
                gutter={4}
                className={cn(
                    // z-index ensures popover appears above page content (matches Combobox)
                    "relative z-[999]",
                    "w-[--popover-anchor-width]",
                    "max-h-96 overflow-auto",
                    "rounded-lg border border-border-base bg-surface-pure shadow-md",
                    "p-1",
                    // Smooth Tailwind UI-style transitions
                    "origin-top transition-all duration-200 ease-out",
                    "data-[open]:scale-100 data-[open]:opacity-100",
                    "data-[closed]:scale-95 data-[closed]:opacity-0",
                )}
                sameWidth
                modal={false}
            >
                {items}
            </AriakitSelectPopover>
        </AriakitSelectProvider>
    );
};

/**
 * Individual item in the select dropdown.
 */
const SelectItem: FC<SelectItemProps> = ({ children, value, className, ...props }) => {
    return (
        <AriakitSelectItem
            value={value}
            className={cn(
                "relative flex w-full cursor-pointer select-none items-center justify-between gap-2",
                "rounded-md px-2 py-1.5",
                "text-base text-content-primary",
                "data-[active-item]:bg-surface-hover",
                "focus:outline-none",
                className,
            )}
            {...props}
        >
            <span className="flex-1">{children}</span>
            <AriakitSelectItemCheck className="flex-none">
                <IconCheckmark2Small className="size-4" />
            </AriakitSelectItemCheck>
        </AriakitSelectItem>
    );
};
SelectItem.displayName = SELECT_ITEM;

type SelectSlots = {
    Item: typeof SelectItem;
};

Select.Item = SelectItem;

export { Select };
